import React from 'react';
import { observer } from 'mobx-react-lite';
import { LiaQuestionCircle } from "react-icons/lia";

import { StatusMessageState } from '../../../../types/chat';

interface StatusIndicatorProps {
    statuses: StatusMessageState[];
}

// Icon components for different actions
const SearchIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
    </svg>
);

const ReadIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);

const ProcessIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const ThinkingIcon = () => (
    <LiaQuestionCircle size="19px" />
);

const RespondingIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const CheckIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20,6 9,17 4,12" />
    </svg>
);

const ErrorIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
);

const CircularProgressIcon = () => (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeDasharray="20 43" />
    </svg>
);

const getActionIcon = (action: string, phase: string) => {
    if (phase === 'complete') return <CheckIcon />;
    if (phase === 'failed') return <ErrorIcon />;
    if (phase === 'progress') return <CircularProgressIcon />;

    switch (action) {
        case 'searching':
            return <SearchIcon />;
        case 'reading':
            return <ReadIcon />;
        case 'thinking':
            return <ThinkingIcon />;
        case 'responding':
            return <RespondingIcon />;
        case 'processing':
        default:
            return <ProcessIcon />;
    }
};

const getStatusColor = (phase: string) => {
    switch (phase) {
        case 'complete':
            return 'text-green-600';
        case 'failed':
            return 'text-red-500';
        default:
            return 'text-gray-500';
    }
};

const StatusIndicator: React.FC<StatusIndicatorProps> = observer(({ statuses }) => {
    if (!statuses || statuses.length === 0) return null;

    return (
        <div className="flex flex-col gap-1 mb-3 ml-1">
            {statuses.map((status, index) => {
                const isInProgress = status.phase === 'start' || status.phase === 'progress';

                if(!status.message?.trim()){ return null }

                return (
                    <div
                        key={`${status.tool}-${index}`}
                        className={`flex items-center gap-2 text-sm transition-all duration-300 ${getStatusColor(status.phase)}`}
                    >
                        <div className={`shrink-0 ${isInProgress ? 'animate-pulse' : ''}`}>
                            {getActionIcon(status.action, status.phase)}
                        </div>
                        <span className="font-medium truncate max-w-md">
                            {status.message}
                            {(status.phase === 'complete' || status.phase === 'failed') &&
                             status.duration > 0 &&
                             status.action !== 'thinking' &&
                             status.action !== 'responding' && (
                                <span className="ml-1 text-gray-400 font-normal">
                                    ({status.duration.toFixed(1)}s)
                                </span>
                            )}
                        </span>
                    </div>
                );
            })}
        </div>
    );
});

export default StatusIndicator;
